import { call, put } from 'redux-saga/effects';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { assertUnreachable } from '~commonInfra/utils/TypescriptHelpers';
import { setDescriptionForItem } from '~src/catalog/restApi/descriptions/setDescriptionForItem';
import { PublishedConnectionTables } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import {
  DescriptionDialogState,
  getDescriptionDialogOptions,
  METRIC_DESCRIPTION_MAXIMUM_LENGTH,
} from '../../../catalog/components/DescriptionDialog';
import { Messages } from '../../../codegen/Localize';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { Metrics } from '../../../metrics/resources/Metrics';
import { PublishedConnections } from '../../../publishedConnection/resources/PublishedConnections';
import { requestSpaceContents } from '../../actions/RequestSpaceContentsActions';
import { SetDescriptionAction } from '../../actions/SetDescriptionAction';
import { IContentInfo } from '../../models/IContentInfo';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';

export function* handleSetDescription(action: SetDescriptionAction) {
  try {
    yield call(openDescriptionDialog, action.item);

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function setDescription(
  item: IContentInfo<'database'> | IContentInfo<'table'> | IContentInfo<'metric'> | IContentInfo<'virtualConnection'> | IContentInfo<'virtualConnectionTable'>,
  state: DescriptionDialogState
): Promise<IResult> {
  if (item.contentType === 'database' || item.contentType === 'table') {
    if (!item.luid) {
      Promise.reject();
    }
  }

  switch (item.contentType) {
    case 'database': return setDescriptionForItem({
      contentType: 'databases',
      luid: item.luid,
      body: {
        database: {
          description: state.description,
        }
      }
    });
    case 'table': return setDescriptionForItem({
      contentType: 'tables',
      luid: item.luid,
      body: {
        table: {
          description: state.description,
        }
      }
    });
    case 'metric': return Metrics.setMetricDescription({
      description: state.description,
      id: item.id,
    });
    case 'virtualConnection': return PublishedConnections.setDescription({
      description: state.description,
      id: item.id,
    });
    case 'virtualConnectionTable': return PublishedConnectionTables.setDescription({
      description: state.description,
      id: item.id,
    });
    default: assertUnreachable(item);
  }
}

export function openDescriptionDialog(item: IContentInfo<'database'> | IContentInfo<'table'> | IContentInfo<'metric'> | IContentInfo<'virtualConnection'> | IContentInfo<'virtualConnectionTable'>): PromiseLike<unknown> {
  let maxCharacterLimit: number;
  let useMarkup;

  switch(item.contentType) {
    case 'database':
    case 'table':
      useMarkup = true;
      maxCharacterLimit = getSessionManagementServiceInstance().maxDescriptionLength();
      break;
    case 'virtualConnection':
      maxCharacterLimit = getSessionManagementServiceInstance().maxDescriptionLength();
      useMarkup = false;
      break;
    case 'virtualConnectionTable':
        maxCharacterLimit = getSessionManagementServiceInstance().maxDescriptionLength();
        useMarkup = false;
        break;
    case 'metric':
      maxCharacterLimit = METRIC_DESCRIPTION_MAXIMUM_LENGTH;
      useMarkup = false;
      break;
    default: assertUnreachable(item);
  }

  const modalInstance = openReactActionDialog(getDescriptionDialogOptions({
    initialBodyProps: { useMarkup, maxCharacterLimit },
    initialFormData: { description: item.description || '' },
    doAction: (formData) => setDescription(item, formData)
  }));
  return modalInstance.result;
}
